import { useCallback, useLayoutEffect, useState } from 'react';
import isChromatic from 'chromatic/isChromatic';
import { generateClassName } from '@klv/shared/utils/functions';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';
import useSlides from '@klv/hooks/useSlides';

import Icon from '@klv/atoms/icon';
import Grid from '@klv/utils/grid';
import { Wrapper } from '@klv/utils/wrapper';

import * as styles from './styles/scrollSocialProof.module.scss';

import type { IScrollSocialProofCustomizable } from './types';
import MixedMedia from '@klv/molecules/mixed-media';
import Buttons from '@klv/molecules/buttons';
import { getBackgroundType } from '@klv/utils/wrapper/functions';

export const ScrollSocialProofCustomizable = ({
  className = '',
  backgroundColor = 'cotton',
  buttons = [],
  customPadding = { customPaddingBottom: 'md', customPaddingTop: 'md' },
  fadeIn = false,
  scrollSocialProofCards = [],
  ...props
}: IScrollSocialProofCustomizable) => {
  const chromatic = isChromatic();
  const { slides, setThisSlide, swipeable } = useSlides(scrollSocialProofCards, ['left', 'right']);
  const { width } = useWindowDimensions(); 
  const isMobile = width <= 768;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [intervalOn, setIntervalOn] = useState(true);

  const updateSlide = useCallback((index: number) => {
    setThisSlide(index);
    setCurrentSlide(index);
  }, [setThisSlide]);

  useLayoutEffect(() => {
    const intervalId = setInterval(() => {
      if (chromatic) {
        setIntervalOn(false);
      }
      if (intervalOn) {
        if (currentSlide < slides.length - 1) {
          updateSlide(currentSlide + 1);
        } else {
          updateSlide(0);
        }
      } else return;
    }, 3000);
  
    return () => {
      clearInterval(intervalId);
    };
  }, [currentSlide, slides]);

  return (
    <div {...swipeable}>
      <Wrapper className={generateClassName([className, styles.scrollSocialProof])} backgroundColor={backgroundColor} fadeIn={fadeIn ? .25 : 0} contained={false} inlinePadding={false} customPadding={customPadding}>
        <Grid className={generateClassName([ styles.customGrid])}>
          <div className={generateClassName([className, styles.navigationButtonContainer])} {...props}>
            <div className={styles.headlineContainer}>
              {slides.length > 0 && slides.map((card, index) => (
                card.headline && <button
                  {...slides[index]}
                  key={`${card.headline}`}
                  type="button"
                  tabIndex={0}
                  role="tab"
                    onClick={() => { updateSlide(index); setIntervalOn(false); }}
                  id={`${card.headline}-${index}`}
                  className={generateClassName([
                    styles.headline,
                    card.$current ? styles.active : '', 
                  ])}
                >
                  {card.headline}
                </button>
              ))}
            </div>
            {(buttons?.length > 0) && 
              <div className={styles.buttonContainer}>
                <Buttons
                  buttons={buttons}
                  variants={['ghost', 'tertiary']}
                  buttonColor={getBackgroundType(backgroundColor) === 'light' ? 'dark' : 'light'}
                />
              </div>
            }
          </div>
          <div className={generateClassName([className, styles.cardContainer])}>
            {scrollSocialProofCards.length > 0 && slides.map((card) => (
                <div
                  key={`${card.headline}-${card.attributionName}`}
                  className={generateClassName([
                    styles.card,
                    card.$current ? styles.displayCard  : styles.hideCard,
                    (styles as Record<string, string>)
                      [`${isMobile ? 'mobile' : 'desktop'}${card.$current ? 'In' : 'Out'}${(card.$dir ?? '').charAt(0).toUpperCase() + (card.$dir ?? '').slice(1)}`],
                  ])}
                >
                  <div className={generateClassName([styles.imgContainer])}>
                    {card.mixedMedia && (
                      <MixedMedia data-chromatic="ignore" mixedMedia={card.mixedMedia} />
                    )}
                    {card.href && (
                      <a
                        className={generateClassName([
                          styles.arrowButton,
                        ])}
                        tabIndex={0}
                        href={card.href}
                      >
                        <Icon fillColor="currentColor" iconName="ChevronDown" />
                      </a> 
                    )}
                  </div>    
                  <div className={generateClassName([styles.textContainer])}>
                    <div className={styles.quote}>{card.quote}</div>
                    <div className={styles.attribution}>{card.attributionName}</div>
                    <div className={styles.credit}>{card.attributionCredentials}</div>
                  </div>
                </div>  
            ))}
          </div>
        </Grid>
      </Wrapper>
    </div>
  );
};
